import { useState } from 'react';
import { plansApi } from '@app/api/organization';
import { logger } from '@app/utils/logger';
import type { ServicePlan, User, TeamMemberAssignment } from '@app/types';

export const useRosterActions = (
  churchId: string | null | undefined,
  user: User | null,
  selectedPlan: ServicePlan | null,
  setSelectedPlan: (plan: ServicePlan | null) => void,
  setAllPlans: React.Dispatch<React.SetStateAction<ServicePlan[]>>,
  setHasUnsavedChanges: (value: boolean) => void,
  setToastMessage: (msg: string) => void,
  setToastType?: (type: 'success' | 'error' | 'info') => void
) => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Helper to combine date and time properly
  const combineDateAndTime = (date: Date, timeString: string): string => {
    const [time, period] = timeString.split(' ');
    const [hours, minutes] = time.split(':');
    let hour = parseInt(hours, 10);
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    
    // Create date with user's local time
    const combined = new Date(date);
    combined.setHours(hour, parseInt(minutes, 10), 0, 0);
    return combined.toISOString();
  };

  const handlePublish = async (
    selectedDate: Date,
    serviceTitle: string,
    localNotes: string,
    localTeam: TeamMemberAssignment[],
    setIsPublishModalOpen: (open: boolean) => void,
    startTime?: string,
    duration?: string,
    serviceType?: string,
    items?: import('@app/types').PlanItem[]
  ) => {
    if (!churchId || !user) return;

    setIsPublishing(true);
    try {
      let planToPublish: ServicePlan;
      // Combine date with time, or use 10:00 AM as default
      const dateTime = startTime ? combineDateAndTime(selectedDate, startTime) : combineDateAndTime(selectedDate, '10:00 AM');

      if (!selectedPlan) {
        planToPublish = await plansApi.create(churchId, {
          churchId,
          date: dateTime,
          title: serviceTitle,
          notes: localNotes,
          duration: duration,
          serviceType: serviceType,
          items: items || [],
          team: localTeam,
          isPublished: true,
          createdBy: user.id,
        });
      } else if (items && items.length > 0) {
        // Use full update to include setlist items (admin_predefined mode)
        planToPublish = await plansApi.update(churchId, selectedPlan.id, {
          date: dateTime,
          title: serviceTitle,
          notes: localNotes,
          duration: duration,
          serviceType: serviceType,
          items,
          team: localTeam,
          isPublished: true,
          isSetlistPublished: true,
        });
      } else {
        // Use roster-only update to preserve setlist items added by worship leader
        planToPublish = await plansApi.updateRoster(churchId, selectedPlan.id, {
          date: dateTime,
          title: serviceTitle,
          notes: localNotes,
          duration: duration,
          serviceType: serviceType,
          team: localTeam,
          isPublished: true,
        });
      }

      setSelectedPlan(planToPublish);
      setAllPlans(prev => {
        const exists = prev.find(p => p.id === planToPublish.id);
        if (exists) {
          return prev.map(p => p.id === planToPublish.id ? planToPublish : p);
        }
        return [...prev, planToPublish];
      });
      setHasUnsavedChanges(false);
      setIsPublishModalOpen(false);
      
      setToastType?.('success');
      setToastMessage('Roster published. Everyone on it has been asked to confirm.');
      setTimeout(() => setToastMessage(''), 3000);
    } catch (error) {
      logger.error('Failed to publish roster:', error);
      setToastType?.('error');
      setToastMessage('Failed to publish roster');
      setTimeout(() => setToastMessage(''), 3000);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleUnpublish = async (
    setConfirmDialog: (dialog: any) => void,
    confirmDialog: any
  ) => {
    if (!selectedPlan || !churchId) return;

    setConfirmDialog({
      isOpen: true,
      title: 'Unpublish Roster',
      message: 'This will hide the roster from team members and mark it as a draft. All data will be kept.',
      type: 'default',
      onConfirm: async () => {
        setIsUnpublishing(true);
        try {
          const updated = await plansApi.update(churchId, selectedPlan.id, {
            ...selectedPlan,
            isPublished: false,
          });
          setSelectedPlan(updated);
          setAllPlans(prev => prev.map(p => p.id === updated.id ? updated : p));
          setHasUnsavedChanges(false);
          setToastType?.('info');
          setToastMessage('Roster unpublished (data kept as draft)');
          setTimeout(() => setToastMessage(''), 3000);
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        } catch (error) {
          logger.error('Failed to unpublish roster:', error);
          setToastType?.('error');
          setToastMessage('Failed to unpublish roster');
          setTimeout(() => setToastMessage(''), 3000);
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        } finally {
          setIsUnpublishing(false);
        }
      },
    });
  };

  const handleDelete = async (
    setConfirmDialog: (dialog: any) => void,
    confirmDialog: any,
    setLocalTeam: (team: TeamMemberAssignment[]) => void,
    setLocalNotes: (notes: string) => void
  ) => {
    if (!churchId || !selectedPlan) return;
    
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Roster',
      message: 'Are you sure you want to PERMANENTLY DELETE this roster from the database? This cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          await plansApi.delete(churchId, selectedPlan.id);
          setAllPlans(prev => prev.filter(p => p.id !== selectedPlan.id));
          setSelectedPlan(null);
          setLocalTeam([]);
          setLocalNotes('');
          setToastType?.('success');
          setToastMessage('Roster permanently deleted');
          setTimeout(() => setToastMessage(''), 3000);
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        } catch (error) {
          logger.error('Failed to delete roster:', error);
          setToastType?.('error');
          setToastMessage('Failed to delete roster');
          setTimeout(() => setToastMessage(''), 3000);
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        } finally {
          setIsDeleting(false);
        }
      },
    });
  };

  return { handlePublish, handleUnpublish, handleDelete, isPublishing, isUnpublishing, isDeleting };
};
